import type { CrmMatchKey, CrmRecordType } from '@/types';
import type {
  CleanupCondition,
  CleanupExclude,
  CleanupFieldMode,
  CleanupKeepRule,
  CleanupMatch,
  CleanupMatchRule,
  CleanupObject,
  CustomCrmTriggerConfig,
  CustomNodeType,
  GraphIssue,
  MergeDuplicatesConfig,
  ReviewDuplicatesConfig,
  ReviewPolicy,
  TriggerConfig,
  TriggerSchedule,
} from '@/types/custom-playbooks';
import { triggerKind } from '@/types/custom-playbooks';
import type { CrmScanStatusDto } from '@/types/gtm-agents';
import { isSalesforceRelativeDate } from '@/utils/playbook-trigger.utils';
import { BRAND } from '@/lib/brand';
import { isRecord } from '@/lib/is-record';

/**
 * CRM cleanup vocabulary for the builder (W5 design, sections 2 and 5). A
 * cleanup play is a `crm_duplicates` trigger (what counts as a duplicate and
 * what never merges), a Review duplicates step (the merge policy) and an
 * optional Merge duplicates step (which record is kept and how blanks fill).
 *
 * LEAF module: types only, no React, and nothing from `node-metadata` or
 * `graph-ops`, which both read it. The backend's `crm_cleanup_rules.py` is the
 * authority; the checks here mirror the ones that need no org data so the
 * canvas can say what is wrong before a save.
 */

export type CleanupCrm = 'salesforce' | 'hubspot';

/** The ruleset version this portal writes (`cleanupVersion`). */
export const CLEANUP_VERSION = 1;

/**
 * The CRMs a cleanup play may run on, in the order a new one picks from:
 * Salesforce, then HubSpot (HubSpot merges can't be undone).
 */
export const CLEANUP_CRM_TYPES: readonly CleanupCrm[] = [
  'salesforce',
  'hubspot',
];

/** A cleanup runs on a clock: never continuously, daily or every few days. */
export const CLEANUP_FREQUENCIES: readonly TriggerSchedule['frequency'][] = [
  'weekly',
  'monthly',
  'quarterly',
];

/** The objects each CRM can check, in the order the editor lists them. */
export const CLEANUP_OBJECTS: Record<CleanupCrm, readonly CleanupObject[]> = {
  salesforce: ['account', 'contact', 'lead'],
  hubspot: ['company', 'contact'],
};

/** The match rules each object takes (design 2.3). */
export const MATCH_RULES: Record<CleanupObject, readonly CleanupMatchRule[]> = {
  account: ['domain', 'name_no_domain'],
  company: ['domain', 'name_no_domain'],
  contact: ['email', 'linkedin', 'cleanlist_id', 'name_in_account'],
  lead: ['email', 'linkedin', 'cleanlist_id', 'name_in_company'],
};

export const KEEP_RULES: readonly CleanupKeepRule[] = [
  'open_deals',
  'owner_is_rep',
  'oldest',
  'newest',
  'recent_activity',
  'most_complete',
];

export const FIELD_MODES: readonly CleanupFieldMode[] = [
  'fill_blanks',
  'keep_master',
];

export const REVIEW_POLICIES: readonly ReviewPolicy[] = [
  'review',
  'auto_exact',
];

export const MAX_CREATED_WITHIN_DAYS = 365;

/** The two steps that only follow a `crm_duplicates` trigger. */
export const CLEANUP_STEP_TYPES: readonly CustomNodeType[] = [
  'review_duplicates',
  'merge_duplicates',
];

/**
 * A cleanup play's steps under the trigger, in the only order they may run:
 * Review duplicates, then an optional Merge duplicates, then an optional
 * Notify (design 2.5, `cleanup_shape`).
 */
export const CLEANUP_LINE: readonly CustomNodeType[] = [
  'review_duplicates',
  'merge_duplicates',
  'notify',
];

/**
 * What each step of the cleanup line may sit right under, as the server's
 * `_cleanup_shape` reads it: Review duplicates under the trigger, Merge
 * duplicates under Review, and Notify under Review or Merge.
 */
const CLEANUP_FOLLOWS: Readonly<Record<string, readonly string[]>> = {
  review_duplicates: ['trigger'],
  merge_duplicates: ['review_duplicates'],
  notify: ['review_duplicates', 'merge_duplicates'],
};

/**
 * Whether a cleanup step fits between two steps of the line: right under
 * `after`, and, when a step sits below the spot, right above `before`.
 */
export function cleanupStepFits(
  type: string,
  after: string | undefined,
  before?: string
): boolean {
  if (!after || !CLEANUP_FOLLOWS[type]?.includes(after)) return false;
  return (
    before === undefined || Boolean(CLEANUP_FOLLOWS[before]?.includes(type))
  );
}

export function isCleanupStep(type: string): boolean {
  return (CLEANUP_STEP_TYPES as readonly string[]).includes(type);
}

export function isCleanupTrigger(
  config: Partial<TriggerConfig> | undefined
): boolean {
  return triggerKind(config) === 'crm_duplicates';
}

/** The CRM a cleanup trigger checks. Only `crmType` travels on its `crm`. */
export function cleanupCrmType(
  config: Partial<TriggerConfig> | undefined
): CleanupCrm | undefined {
  const crmType = config?.crm?.crmType;
  return crmType === 'salesforce' || crmType === 'hubspot'
    ? crmType
    : undefined;
}

/** The connected CRM a cleanup play may run on, if the org has one. */
export function cleanupCrmOf(
  connectedCrmTypes: readonly string[]
): CleanupCrm | undefined {
  return CLEANUP_CRM_TYPES.find((crmType) =>
    connectedCrmTypes.includes(crmType)
  );
}

/**
 * What a new cleanup trigger starts from in one org (design 2.4): the CRM it
 * runs on, and that CRM's settings the defaults follow. `matchKeys` is the
 * org's match cascade (null is the backend's full cascade), and `recordType`
 * is what a new person is created as, which adds Leads when it is Lead.
 * `useCleanupSeed` reads it; a new cleanup trigger is only offered once it has.
 */
export interface CleanupSeed {
  crmType: CleanupCrm;
  matchKeys: readonly CrmMatchKey[] | null;
  recordType: CrmRecordType | null;
}

/* ── Defaults (mirror of `default_rules`, design 2.4) ───────────────── */

/**
 * The backend's full match cascade, which a null `match_keys` stands for
 * (`general-tab.component.tsx` shows the same order).
 */
const DEFAULT_MATCH_CASCADE: readonly CrmMatchKey[] = [
  'cleanlist_id',
  'email',
  'linkedin',
  'name_company',
];

/**
 * The spellings of a match key the backend takes for it
 * (`crm_push._MATCH_KEY_ALIASES`).
 */
const MATCH_KEY_ALIASES: Readonly<Record<string, CrmMatchKey>> = {
  cleanlist_id: 'cleanlist_id',
  cleanlist: 'cleanlist_id',
  id: 'cleanlist_id',
  email: 'email',
  linkedin: 'linkedin',
  linkedin_url: 'linkedin',
  public_linkedin_url: 'linkedin',
  name_company: 'name_company',
  name: 'name_company',
  'name+company': 'name_company',
  first_last_company: 'name_company',
};

/**
 * The org's match cascade as the server reads it (`normalize_match_keys`):
 * each key's other spellings mapped to it, unknown keys dropped, each key
 * once, in order. A missing or empty list, or one with no key it knows, is
 * the full cascade, so a bad settings row never turns matching off.
 */
export function normalizeMatchKeys(
  keys: readonly unknown[] | null | undefined
): readonly CrmMatchKey[] {
  const out: CrmMatchKey[] = [];
  for (const raw of keys ?? []) {
    if (!raw) continue;
    const spelling = String(raw).trim().toLowerCase();
    const key = Object.prototype.hasOwnProperty.call(
      MATCH_KEY_ALIASES,
      spelling
    )
      ? MATCH_KEY_ALIASES[spelling]
      : undefined;
    if (key && !out.includes(key)) out.push(key);
  }
  return out.length ? out : DEFAULT_MATCH_CASCADE;
}

function isCompanyObject(object: CleanupObject): boolean {
  return object === 'account' || object === 'company';
}

/**
 * Salesforce Account and Contact, plus Lead when the org creates Leads;
 * HubSpot company and contact.
 */
export function defaultCleanupObjects(
  crmType: CleanupCrm,
  recordType?: CrmRecordType | null
): CleanupObject[] {
  if (crmType === 'hubspot') return ['company', 'contact'];
  return recordType === 'Lead'
    ? ['account', 'contact', 'lead']
    : ['account', 'contact'];
}

/**
 * Match rules for one object. Accounts match on domain and on name when there
 * is no domain. People's exact keys follow the org's match cascade (a key the
 * org dropped is off), and a contact's name rule is on when the cascade holds
 * `name_company`. A lead's name rule starts off (design 2.2).
 */
export function defaultMatch(
  object: CleanupObject,
  matchKeys?: readonly CrmMatchKey[] | null
): Partial<Record<CleanupMatchRule, boolean>> {
  if (isCompanyObject(object)) return { domain: true, name_no_domain: true };
  const cascade = normalizeMatchKeys(matchKeys);
  const on = (key: CrmMatchKey) => cascade.includes(key);
  const exact = {
    email: on('email'),
    linkedin: on('linkedin'),
    cleanlist_id: on('cleanlist_id'),
  };
  return object === 'contact'
    ? { ...exact, name_in_account: on('name_company') }
    : { ...exact, name_in_company: false };
}

/** Keep order: accounts by open deals, a real rep, then the oldest; people by a real rep, then the oldest. */
export function defaultKeep(object: CleanupObject): CleanupKeepRule[] {
  return isCompanyObject(object)
    ? ['open_deals', 'owner_is_rep', 'oldest']
    : ['owner_is_rep', 'oldest'];
}

export function emptyExclude(): CleanupExclude {
  return { ownerIds: [], createdWithinDays: 0, conditions: {} };
}

/** The cleanup template's cadence: quarterly at 09:00. */
export function defaultCleanupSchedule(timezone?: string): TriggerSchedule {
  return { frequency: 'quarterly', time: '09:00', timezone };
}

/**
 * A fresh `crm_duplicates` trigger in the design 2.2 wire shape. The
 * schedule is kept when it is one a cleanup may run on.
 */
export function cleanupTriggerConfig(
  crmType: CleanupCrm,
  options: {
    objects?: readonly CleanupObject[];
    matchKeys?: readonly CrmMatchKey[] | null;
    recordType?: CrmRecordType | null;
    schedule?: TriggerSchedule;
  } = {}
): TriggerConfig {
  const objects = [
    ...(options.objects ?? defaultCleanupObjects(crmType, options.recordType)),
  ];
  const match: CleanupMatch = {};
  for (const object of objects)
    match[object] = defaultMatch(object, options.matchKeys);
  const schedule =
    options.schedule && CLEANUP_FREQUENCIES.includes(options.schedule.frequency)
      ? options.schedule
      : defaultCleanupSchedule(options.schedule?.timezone);
  return {
    source: 'crm_duplicates',
    kind: 'crm_duplicates',
    // Only the CRM travels on a cleanup trigger's `crm` (design 2.2).
    crm: { crmType } as CustomCrmTriggerConfig,
    schedule,
    cleanupVersion: CLEANUP_VERSION,
    objects,
    match,
    exclude: emptyExclude(),
  };
}

export function defaultReviewConfig(): ReviewDuplicatesConfig {
  return { policy: 'review' };
}

/** Keep order and fill blanks for each object the trigger checks. */
export function mergeConfigFor(
  objects: readonly CleanupObject[]
): MergeDuplicatesConfig {
  const config: MergeDuplicatesConfig = { keep: {}, fields: {} };
  for (const object of objects) {
    config.keep[object] = defaultKeep(object);
    config.fields[object] = 'fill_blanks';
  }
  return config;
}

/* ── Words ───────────────────────────────────────────────────────────── */

const OBJECT_NOUN: Record<CleanupObject, [string, string]> = {
  account: ['account', 'accounts'],
  company: ['company', 'companies'],
  contact: ['contact', 'contacts'],
  lead: ['lead', 'leads'],
};

function isCleanupObject(object: string): object is CleanupObject {
  return Object.prototype.hasOwnProperty.call(OBJECT_NOUN, object);
}

/** "account" or "accounts"; an unknown object reads as itself. */
export function cleanupObjectNoun(object: string, plural = true): string {
  const noun = OBJECT_NOUN[object as CleanupObject];
  if (!noun) return object;
  return plural ? noun[1] : noun[0];
}

/** "a", "a and b", "a, b and c". */
export function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1];
}

/** "Salesforce" or "HubSpot"; Salesforce when the CRM isn't known. */
export function cleanupCrmName(crmType: CleanupCrm | undefined): string {
  return crmType === 'hubspot' ? 'HubSpot' : 'Salesforce';
}

/**
 * What a cleanup trigger checks: "Salesforce accounts and contacts". One with
 * no list checks its CRM's defaults, which follow the org's settings, so it
 * reads as the CRM's records.
 */
export function cleanupScopeWords(config: Partial<TriggerConfig>): string {
  const crm = cleanupCrmName(cleanupCrmType(config));
  if (config.objects === undefined) return crm + ' records';
  const objects = (Array.isArray(config.objects) ? config.objects : []).map(
    (object) => cleanupObjectNoun(object)
  );
  if (!objects.length) return 'Pick records to check';
  return crm + ' ' + joinWords(objects);
}

export const REVIEW_POLICY_LABEL: Record<ReviewPolicy, string> = {
  review: 'Review first',
  auto_exact: 'Auto-merge exact contacts',
};

/**
 * A merge policy in words, for the play its trigger checks. Auto merges
 * exact contact and lead groups (never accounts or companies), so once the
 * play checks leads, or a Salesforce trigger with no list whose defaults may
 * add them, it says records: "Auto-merge exact records".
 */
export function reviewPolicyLabel(
  policy: ReviewPolicy | undefined,
  trigger?: Partial<TriggerConfig>
): string {
  if (policy !== 'auto_exact') return REVIEW_POLICY_LABEL.review;
  if (!isCleanupTrigger(trigger)) return REVIEW_POLICY_LABEL.auto_exact;
  const leads = Array.isArray(trigger?.objects)
    ? trigger.objects.includes('lead')
    : (cleanupCrmType(trigger) ?? 'salesforce') === 'salesforce';
  return leads ? 'Auto-merge exact records' : REVIEW_POLICY_LABEL.auto_exact;
}

/** A merge step's field rule in a few words. */
export function mergeFieldsWords(
  config: Partial<MergeDuplicatesConfig>
): string {
  const modes = Object.values(config.fields ?? {});
  if (modes.length && modes.every((mode) => mode === 'keep_master'))
    return 'Leave the kept record as is';
  if (modes.some((mode) => mode === 'keep_master'))
    return 'Fill blanks on some records';
  return 'Fill blanks on the kept record';
}

/* ── Checks (mirror of `validate_rules` without a describe, design 2.5) ─ */

/**
 * The exclusion operators the server reads (`crm_cleanup_rules.py`
 * `CONDITION_OPERATORS`), and the other spellings it takes for them: the
 * builder writes `greater_than`, Clu may write `gt` or `eq`.
 */
const CONDITION_OPERATORS = new Set([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'in',
  'not_in',
  'includes',
  'excludes',
  'greater',
  'less',
  'gte',
  'lte',
  'exists',
  'not_exists',
]);
const OPERATOR_SPELLINGS: Readonly<Record<string, string>> = {
  eq: 'equals',
  equal: 'equals',
  neq: 'not_equals',
  ne: 'not_equals',
  gt: 'greater',
  lt: 'less',
  greater_than: 'greater',
  less_than: 'less',
  greater_than_or_equal: 'gte',
  less_than_or_equal: 'lte',
};
const VALUELESS_OPS = new Set(['exists', 'not_exists']);
const LIST_OPS = new Set(['in', 'not_in', 'includes', 'excludes']);

/** A condition's operator as the server reads it, or undefined. */
export function conditionOperator(
  condition: Partial<CleanupCondition> & { operator?: unknown }
): string | undefined {
  const raw = condition.op ?? condition.operator;
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  const op = raw.trim().toLowerCase();
  const canonical = OPERATOR_SPELLINGS[op] ?? op;
  return CONDITION_OPERATORS.has(canonical) ? canonical : undefined;
}

/** A single value: not blank, not a list, not an object. */
function isScalar(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'object') return false;
  return String(value).trim() !== '';
}

/**
 * A condition the server can read (`canonical_condition`): a field, an
 * operator it knows, and a value that operator takes. A list operator takes
 * a list of values or a string of them split on ";"; any other takes one.
 */
function conditionComplete(
  condition: Partial<CleanupCondition> | null | undefined
): boolean {
  if (!condition || typeof condition !== 'object') return false;
  if (typeof condition.field !== 'string' || !condition.field.trim())
    return false;
  const op = conditionOperator(condition);
  if (!op) return false;
  if (VALUELESS_OPS.has(op)) return true;
  const value: unknown = condition.value;
  if (LIST_OPS.has(op)) {
    const items = typeof value === 'string' ? value.split(';') : value;
    return Array.isArray(items) && items.length > 0 && items.every(isScalar);
  }
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  return isScalar(value);
}

/**
 * A Salesforce relative date (`TODAY`, `LAST_N_DAYS:30`) as a condition's
 * value. The scan compares exact values and can't read one, so the
 * exclusion would never match. A single word counts only in Salesforce's
 * capitals, so a picklist value such as "Today" stays a value.
 */
function isRelativeDateValue(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const token = value.trim();
  return (
    isSalesforceRelativeDate(token) &&
    (token.includes('_') || token === token.toUpperCase())
  );
}

function issue(nodeId: string, code: string, message: string): GraphIssue {
  return { nodeId, code, message };
}

/**
 * The trigger's own checks: the CRM, the schedule, the version, the objects,
 * the match rules and the exclusions. All warnings: they block On and Run
 * Test, never a draft save.
 */
export function cleanupTriggerIssues(
  config: Partial<TriggerConfig>,
  nodeId: string
): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const crmType = cleanupCrmType(config);

  const frequency = config.schedule?.frequency;
  if (!frequency || !CLEANUP_FREQUENCIES.includes(frequency))
    issues.push(
      issue(
        nodeId,
        'cleanup_schedule_required',
        'Run a cleanup weekly, monthly or quarterly.'
      )
    );

  const version: unknown = config.cleanupVersion;
  if (
    version !== undefined &&
    (typeof version !== 'number' ||
      !Number.isInteger(version) ||
      version < 1 ||
      version > CLEANUP_VERSION)
  )
    issues.push(
      issue(
        nodeId,
        'cleanup_version',
        `This cleanup was set up in a newer version of ${BRAND.name}. Reload the page.`
      )
    );

  // A trigger with no list checks its CRM's defaults
  // (`canonical_trigger_rules`), and the server checks the list only when it
  // is there (`validate_rules`). A list that is there must name records.
  const listed = config.objects !== undefined;
  const objects: string[] = Array.isArray(config.objects)
    ? [...config.objects]
    : [];
  const allowed: readonly string[] = crmType ? CLEANUP_OBJECTS[crmType] : [];
  if (listed && !objects.length) {
    issues.push(
      issue(nodeId, 'cleanup_objects', 'Pick which records to check.')
    );
  } else if (crmType === 'hubspot' && objects.includes('lead')) {
    issues.push(
      issue(nodeId, 'cleanup_objects', 'HubSpot has no leads to check.')
    );
  } else if (objects.some((object) => !allowed.includes(object))) {
    issues.push(
      issue(
        nodeId,
        'cleanup_objects',
        cleanupCrmName(crmType) + " can't check one of the picked records."
      )
    );
  }

  // Without a list, the server reads the match rules and the exclusions
  // against the objects the match block names.
  const named = listed
    ? objects
    : isRecord(config.match)
      ? Object.keys(config.match)
      : [];
  const checked = [...new Set(named)].filter((object) =>
    allowed.includes(object)
  );
  issues.push(...matchIssues(config.match, checked, nodeId));
  // With no object it can check, the server reads exclusions against every
  // object the CRM has.
  issues.push(
    ...excludeIssues(config.exclude, checked.length ? checked : allowed, nodeId)
  );
  return issues;
}

/**
 * Match rules left out take their default, which the org's match cascade may
 * turn on; only a lead's company name rule is never on unasked. So an object
 * with no match rules written matches on its defaults, and one reads empty
 * only when every rule it takes is off or can't default on
 * (`_validate_match`).
 */
const NEVER_ON_BY_DEFAULT: ReadonlySet<CleanupMatchRule> = new Set([
  'name_in_company',
]);

function matchIssues(
  match: unknown,
  objects: readonly string[],
  nodeId: string
): GraphIssue[] {
  if (match === undefined) return [];
  if (!isRecord(match))
    return [
      issue(
        nodeId,
        'cleanup_match_empty',
        'Choose what counts as a duplicate.'
      ),
    ];
  const issues: GraphIssue[] = [];
  for (const object of objects) {
    const allowed = MATCH_RULES[object as CleanupObject];
    const rules = match[object];
    if (!allowed || rules === undefined || rules === null) continue;
    if (!isRecord(rules)) {
      issues.push(
        issue(
          nodeId,
          'cleanup_match_empty',
          'Turn on a match rule for ' + cleanupObjectNoun(object) + '.'
        )
      );
      continue;
    }
    if (
      Object.entries(rules).some(
        ([rule, value]) =>
          !(allowed as readonly string[]).includes(rule) ||
          typeof value !== 'boolean'
      )
    )
      issues.push(
        issue(
          nodeId,
          'cleanup_match_rule',
          'A match rule for ' + cleanupObjectNoun(object) + " isn't recognized."
        )
      );
    const canMatch = allowed.some(
      (rule) =>
        rules[rule] === true ||
        (!(rule in rules) && !NEVER_ON_BY_DEFAULT.has(rule))
    );
    if (!canMatch)
      issues.push(
        issue(
          nodeId,
          'cleanup_match_empty',
          'Turn on a match rule for ' + cleanupObjectNoun(object) + '.'
        )
      );
  }
  return issues;
}

function excludeIssues(
  exclude: Partial<CleanupExclude> | undefined,
  objects: readonly string[],
  nodeId: string
): GraphIssue[] {
  if (exclude === undefined) return [];
  if (!isRecord(exclude))
    return [
      issue(nodeId, 'cleanup_exclude', 'Finish or remove each exclusion.'),
    ];
  const issues: GraphIssue[] = [];
  const days = exclude?.createdWithinDays;
  if (
    days !== undefined &&
    (typeof days !== 'number' ||
      !Number.isInteger(days) ||
      days < 0 ||
      days > MAX_CREATED_WITHIN_DAYS)
  )
    issues.push(
      issue(
        nodeId,
        'cleanup_exclude',
        'Created within needs a whole number of days from 0 to 365.'
      )
    );
  const owners = exclude?.ownerIds;
  if (
    owners !== undefined &&
    (!Array.isArray(owners) ||
      owners.some((owner) => typeof owner !== 'string' || !owner.trim()))
  )
    issues.push(
      issue(nodeId, 'cleanup_exclude', "An excluded owner isn't recognized.")
    );
  // Left out reads as none; null is not grouped by object
  // (`exclude.get("conditions", {})`).
  const byObject: unknown =
    exclude.conditions === undefined ? {} : exclude.conditions;
  if (!isRecord(byObject)) {
    issues.push(
      issue(
        nodeId,
        'cleanup_exclude',
        'Finish or remove each exclusion condition.'
      )
    );
    return issues;
  }
  const unchecked = Object.keys(byObject).filter(
    (object) => !objects.includes(object)
  );
  if (unchecked.length)
    issues.push(
      issue(
        nodeId,
        'cleanup_exclude',
        'Remove the exclusions for ' +
          joinWords(unchecked.map((object) => cleanupObjectNoun(object))) +
          ", which this cleanup doesn't check."
      )
    );
  const conditions = Object.entries(byObject)
    .filter(([object]) => objects.includes(object))
    .map(([, list]) => list);
  if (
    conditions.some(
      (list) =>
        !Array.isArray(list) ||
        list.some(
          (condition: Partial<CleanupCondition>) =>
            !conditionComplete(condition)
        )
    )
  )
    issues.push(
      issue(
        nodeId,
        'cleanup_exclude',
        'Finish or remove each exclusion condition.'
      )
    );
  if (
    conditions.some(
      (list) =>
        Array.isArray(list) &&
        list.some((condition: Partial<CleanupCondition> | null) =>
          isRelativeDateValue(condition?.value)
        )
    )
  )
    issues.push(
      issue(
        nodeId,
        'cleanup_exclude',
        'Use an exact date in exclusion conditions.'
      )
    );
  return issues;
}

/**
 * A policy the server doesn't know (`cleanup_policy`), and auto-merge with
 * nothing to merge through (`cleanup_auto_without_merge`).
 */
export function reviewIssues(
  config: Partial<ReviewDuplicatesConfig>,
  nodeId: string,
  hasMergeStep: boolean
): GraphIssue[] {
  if (
    'policy' in config &&
    !(REVIEW_POLICIES as readonly unknown[]).includes(config.policy)
  )
    return [
      issue(
        nodeId,
        'cleanup_policy',
        'Pick ' +
          REVIEW_POLICY_LABEL.review +
          ' or ' +
          REVIEW_POLICY_LABEL.auto_exact +
          '.'
      ),
    ];
  if (config.policy === 'auto_exact' && !hasMergeStep)
    return [
      issue(
        nodeId,
        'cleanup_auto_without_merge',
        'Auto-merge needs a Merge duplicates step.'
      ),
    ];
  return [];
}

/**
 * Unknown or repeated keep rules, an object with none, a rule its object
 * can't take (open deals on people), unknown field rules, and keep or field
 * rules for an object the cleanup's CRM doesn't have, once it is known.
 */
export function mergeIssues(
  config: Partial<MergeDuplicatesConfig>,
  nodeId: string,
  crmType?: CleanupCrm
): GraphIssue[] {
  const issues: GraphIssue[] = [];
  if (config.keep !== undefined && !isRecord(config.keep))
    issues.push(issue(nodeId, 'cleanup_keep', 'Pick which record to keep.'));
  if (config.fields !== undefined && !isRecord(config.fields))
    issues.push(issue(nodeId, 'cleanup_fields', 'Pick how blank fields fill.'));
  if (issues.length) return issues;
  if (crmType) {
    const known: readonly string[] = CLEANUP_OBJECTS[crmType];
    const foreign = [
      ...new Set([
        ...Object.keys(config.keep ?? {}),
        ...Object.keys(config.fields ?? {}),
      ]),
    ].filter((object) => !known.includes(object));
    if (foreign.length)
      issues.push(
        issue(
          nodeId,
          'cleanup_keep',
          cleanupCrmName(crmType) +
            ' has no ' +
            joinWords(foreign.map((object) => cleanupObjectNoun(object))) +
            ' to merge.'
        )
      );
  }
  const entries = Object.entries(config.keep ?? {});
  const lists = entries.map(([, list]) => list);
  const known = (rule: unknown) =>
    (KEEP_RULES as readonly unknown[]).includes(rule);
  if (lists.some((list) => !Array.isArray(list) || !list.every(known))) {
    issues.push(issue(nodeId, 'cleanup_keep', "A keep rule isn't recognized."));
  } else if (lists.some((list) => new Set(list).size !== list.length)) {
    issues.push(
      issue(nodeId, 'cleanup_keep', 'Each keep rule can be used once.')
    );
  }
  for (const [object, list] of entries) {
    if (!Array.isArray(list)) continue;
    if (!list.length) {
      issues.push(
        issue(
          nodeId,
          'cleanup_keep',
          'Pick which ' + cleanupObjectNoun(object, false) + ' to keep.'
        )
      );
      continue;
    }
    // Open deals is the one rule an object can be refused (`keepRulesFor`).
    const allowed: readonly unknown[] = isCleanupObject(object)
      ? keepRulesFor(object)
      : KEEP_RULES;
    if (list.some((rule) => known(rule) && !allowed.includes(rule)))
      issues.push(
        issue(
          nodeId,
          'cleanup_keep',
          cleanupObjectTitle(object) + ' have no open deals to keep by.'
        )
      );
  }
  const modes = Object.values(config.fields ?? {});
  if (modes.some((mode) => !(FIELD_MODES as readonly unknown[]).includes(mode)))
    issues.push(
      issue(nodeId, 'cleanup_fields', "A field rule isn't recognized.")
    );
  return issues;
}

/** Which rules each cleanup step holds (`STEP_RULE_KEYS`). */
const STEP_RULE_KEYS: Readonly<Record<string, readonly string[]>> = {
  trigger: ['objects', 'match', 'exclude', 'cleanupVersion'],
  review_duplicates: ['policy'],
  merge_duplicates: ['keep', 'fields'],
};

const STEP_NAME: Readonly<Record<string, string>> = {
  trigger: 'CRM duplicates trigger',
  review_duplicates: 'Review duplicates step',
  merge_duplicates: 'Merge duplicates step',
};

/**
 * A rule written on a step that doesn't hold it, which the server reads
 * from its own step only, so here it does nothing
 * (`cleanup_rule_misplaced`).
 */
export function misplacedRuleIssues(
  config: object | undefined,
  step: 'trigger' | 'review_duplicates' | 'merge_duplicates',
  nodeId: string
): GraphIssue[] {
  if (!isRecord(config)) return [];
  const issues: GraphIssue[] = [];
  for (const [owner, keys] of Object.entries(STEP_RULE_KEYS)) {
    if (owner === step || !keys.some((key) => key in config)) continue;
    issues.push(
      issue(
        nodeId,
        'cleanup_rule_misplaced',
        'This step holds a rule of the ' +
          STEP_NAME[owner] +
          ', which does nothing here.'
      )
    );
  }
  return issues;
}

/* ── The rules editor (design 10.2, slice S8) ──────────────────────── */

/** "Accounts", "Contact": an object's noun with a capital. */
export function cleanupObjectTitle(object: string, plural = true): string {
  const noun = cleanupObjectNoun(object, plural);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

export const MATCH_RULE_LABEL: Record<CleanupMatchRule, string> = {
  domain: 'Domain',
  name_no_domain: 'Name without a domain',
  email: 'Email',
  linkedin: 'LinkedIn URL',
  cleanlist_id: 'Cleanlist ID',
  name_in_account: 'Name in the same account',
  name_in_company: 'Name at the same company',
};

/**
 * The match rules whose default follows the org's match cascade (design 2.4):
 * people's exact keys, and a contact's name rule. An account's rules and a
 * lead's name rule start the same in every org.
 */
const SETTINGS_MATCH_RULES: Partial<
  Record<CleanupObject, readonly CleanupMatchRule[]>
> = {
  contact: ['email', 'linkedin', 'cleanlist_id', 'name_in_account'],
  lead: ['email', 'linkedin', 'cleanlist_id'],
};

/**
 * Whether a match rule reads "From CRM settings": it follows the org's match
 * cascade, and it is still set the way the cascade sets it.
 */
export function matchRuleFromSettings(
  object: CleanupObject,
  rule: CleanupMatchRule,
  on: boolean,
  matchKeys: readonly CrmMatchKey[] | null
): boolean {
  if (!SETTINGS_MATCH_RULES[object]?.includes(rule)) return false;
  return (defaultMatch(object, matchKeys)[rule] === true) === on;
}

/**
 * Whether turning the object on takes match rules from the org's settings:
 * people's exact keys and a contact's name rule follow its match cascade.
 */
export function objectTakesSettings(object: CleanupObject): boolean {
  return Boolean(SETTINGS_MATCH_RULES[object]?.length);
}

/**
 * Whether an edit to a cleanup trigger would take a default from the org's
 * CRM settings (design 2.4): it lists no objects, so the server checks its
 * CRM's defaults (with Leads for an org that creates them), or an object it
 * checks leaves out a match rule whose default follows the org's cascade.
 * One that lists its objects and sets each of those rules reads the same
 * under any settings.
 */
export function triggerTakesSettings(
  config: Partial<TriggerConfig> | undefined
): boolean {
  if (!Array.isArray(config?.objects)) return true;
  const match: unknown = config.match;
  return cleanupObjectsOf(config).some((object) => {
    const rules: unknown = isRecord(match) ? match[object] : undefined;
    return (SETTINGS_MATCH_RULES[object] ?? []).some(
      (rule) => !isRecord(rules) || typeof rules[rule] !== 'boolean'
    );
  });
}

/** The keep rules an object can take: open deals only rank companies. */
export function keepRulesFor(object: CleanupObject): CleanupKeepRule[] {
  return isCompanyObject(object)
    ? [...KEEP_RULES]
    : KEEP_RULES.filter((rule) => rule !== 'open_deals');
}

export function keepRuleLabel(
  rule: CleanupKeepRule,
  crmType: CleanupCrm | undefined
): string {
  switch (rule) {
    case 'open_deals':
      return crmType === 'hubspot'
        ? 'Most open deals'
        : 'Most open opportunities';
    case 'owner_is_rep':
      return 'Owned by a rep';
    case 'oldest':
      return 'Oldest';
    case 'newest':
      return 'Newest';
    case 'recent_activity':
      return 'Latest activity';
    case 'most_complete':
      return 'Most complete';
  }
}

/**
 * The objects a trigger checks that its CRM can check, once each, in the
 * CRM's order. A trigger with no list checks the CRM's defaults, as the
 * server reads it (`canonical_trigger_rules`), which add Leads when the org
 * creates them (`recordType`, from `useCleanupSeed`).
 */
export function cleanupObjectsOf(
  config: Partial<TriggerConfig> | undefined,
  recordType?: CrmRecordType | null
): CleanupObject[] {
  const crmType = cleanupCrmType(config) ?? CLEANUP_CRM_TYPES[0];
  const picked: readonly string[] = Array.isArray(config?.objects)
    ? config.objects
    : defaultCleanupObjects(crmType, recordType);
  return CLEANUP_OBJECTS[crmType].filter((object) => picked.includes(object));
}

/** Every rule of the object as a boolean; a rule not set takes its default. */
function canonicalMatch(
  object: CleanupObject,
  current: Partial<Record<CleanupMatchRule, boolean>> | undefined,
  matchKeys: readonly CrmMatchKey[] | null | undefined
): Partial<Record<CleanupMatchRule, boolean>> {
  const defaults = defaultMatch(object, matchKeys);
  const out: Partial<Record<CleanupMatchRule, boolean>> = {};
  for (const rule of MATCH_RULES[object]) {
    const value = current?.[rule];
    out[rule] = typeof value === 'boolean' ? value : defaults[rule] === true;
  }
  return out;
}

/**
 * `{ field, op, value }`, with no value on an operator that takes none. The
 * operator is read as the server reads it (`condition_operator`): `op`, or
 * `operator` when there is no `op`, the spelling of a CRM condition Clu
 * writes. It is written back as `op`, in the spelling it came in.
 */
export function canonicalCondition(
  condition: (Partial<CleanupCondition> & { operator?: unknown }) | undefined
): CleanupCondition {
  const raw = condition?.op ?? condition?.operator;
  const out: CleanupCondition = {
    field: typeof condition?.field === 'string' ? condition.field : '',
    op: typeof raw === 'string' ? raw : '',
  };
  const op = condition ? conditionOperator(condition) : undefined;
  if (!(op && VALUELESS_OPS.has(op)) && condition?.value !== undefined)
    out.value = condition.value;
  return out;
}

function canonicalExclude(
  exclude: Partial<CleanupExclude> | undefined,
  objects: readonly CleanupObject[]
): CleanupExclude {
  const ownerIds: string[] = [];
  for (const owner of Array.isArray(exclude?.ownerIds)
    ? exclude.ownerIds
    : []) {
    const id = typeof owner === 'string' ? owner.trim() : '';
    if (id && !ownerIds.includes(id)) ownerIds.push(id);
  }
  const days = exclude?.createdWithinDays;
  const createdWithinDays =
    typeof days === 'number' && Number.isFinite(days)
      ? Math.min(MAX_CREATED_WITHIN_DAYS, Math.max(0, Math.round(days)))
      : 0;
  const conditions: CleanupExclude['conditions'] = {};
  for (const object of objects) {
    const list = exclude?.conditions?.[object];
    if (Array.isArray(list) && list.length)
      conditions[object] = list.map(canonicalCondition);
  }
  return { ownerIds, createdWithinDays, conditions };
}

/**
 * A cleanup trigger as the editor writes it (design 2.2): only the CRM on
 * `crm`, the objects its CRM can check in that CRM's order, a full match
 * block for each of them and no other, and exclusions for those objects
 * only. A rule that isn't set takes its default under `matchKeys`, the org's
 * cascade, and a trigger with no list takes its CRM's objects under
 * `recordType`, so the first edit keeps the exclusions of the objects the
 * server checks. A `cleanupVersion` newer than this portal's stays, so the
 * version warning keeps showing.
 */
export function canonicalCleanupTrigger(
  config: Partial<TriggerConfig>,
  matchKeys?: readonly CrmMatchKey[] | null,
  recordType?: CrmRecordType | null
): TriggerConfig {
  const crmType = cleanupCrmType(config) ?? CLEANUP_CRM_TYPES[0];
  const objects = cleanupObjectsOf(config, recordType);
  const match: CleanupMatch = {};
  for (const object of objects)
    match[object] = canonicalMatch(object, config.match?.[object], matchKeys);
  const version =
    typeof config.cleanupVersion === 'number' &&
    config.cleanupVersion > CLEANUP_VERSION
      ? config.cleanupVersion
      : CLEANUP_VERSION;
  const next: TriggerConfig = {
    source: 'crm_duplicates',
    kind: 'crm_duplicates',
    crm: { crmType } as CustomCrmTriggerConfig,
    cleanupVersion: version,
    objects,
    match,
    exclude: canonicalExclude(config.exclude, objects),
  };
  if (config.schedule) next.schedule = config.schedule;
  return next;
}

/**
 * The objects a Merge step keeps and fills for: the ones its trigger checks
 * (its CRM's defaults under `recordType` when it lists none), or, without a
 * cleanup trigger in reach, the ones it already holds.
 */
export function mergeObjectsOf(
  trigger: Partial<TriggerConfig> | undefined,
  config: Partial<MergeDuplicatesConfig>,
  recordType?: CrmRecordType | null
): CleanupObject[] {
  if (trigger && isCleanupTrigger(trigger))
    return cleanupObjectsOf(trigger, recordType);
  const held = new Set([
    ...Object.keys(config.keep ?? {}),
    ...Object.keys(config.fields ?? {}),
  ]);
  const order: CleanupObject[] = ['account', 'company', 'contact', 'lead'];
  return order.filter((object) => held.has(object));
}

/**
 * A Merge step's config as the editor writes it (design 2.2): a keep order
 * and a field rule for each object, and no other. An object without a keep
 * order takes the default; unknown and repeated keep rules drop, and so do
 * the ones the object can't take (`keepRulesFor`); a field rule other than
 * `keep_master` reads as fill blanks.
 */
export function canonicalMergeConfig(
  config: Partial<MergeDuplicatesConfig>,
  objects: readonly CleanupObject[]
): MergeDuplicatesConfig {
  const out: MergeDuplicatesConfig = { keep: {}, fields: {} };
  for (const object of objects) {
    const held = config.keep?.[object];
    const list: readonly unknown[] = Array.isArray(held)
      ? held
      : defaultKeep(object);
    const allowed: readonly unknown[] = keepRulesFor(object);
    const keep: CleanupKeepRule[] = [];
    for (const rule of list)
      if (allowed.includes(rule) && !keep.includes(rule as CleanupKeepRule))
        keep.push(rule as CleanupKeepRule);
    out.keep[object] = keep;
    out.fields[object] =
      config.fields?.[object] === 'keep_master' ? 'keep_master' : 'fill_blanks';
  }
  return out;
}

/** A Review duplicates step's config: review unless it says auto. */
export function canonicalReviewConfig(
  config: Partial<ReviewDuplicatesConfig> | undefined
): ReviewDuplicatesConfig {
  return { policy: config?.policy === 'auto_exact' ? 'auto_exact' : 'review' };
}

/* ── The scan line (design 3.2) ──────────────────────────────────────── */

export type ScanTone = 'ok' | 'muted' | 'warn';

function records(count: number): string {
  return count.toLocaleString('en-US') + (count === 1 ? ' record' : ' records');
}

function aboutMinutes(minutes: number): string {
  if (minutes < 90) return 'about ' + minutes + ' min';
  const hours = Math.round(minutes / 60);
  return 'about ' + hours + ' hours';
}

/**
 * The trigger's scan status in a few words: "Ready · 12,400 records",
 * "Scanning · 4,200 records", "Queued · about 40 min", "Salesforce not
 * connected". A finished scan the next run scans again first (a merge came
 * after it, or the play now checks more) is "Scan out of date · 12,400
 * records", never "Not scanned yet". A run waiting on the scan and objects it
 * couldn't read follow.
 */
export function scanStatusLine(scan: CrmScanStatusDto): {
  text: string;
  tone: ScanTone;
} {
  const parts: string[] = [];
  let tone: ScanTone = 'muted';
  const count = scan.recordsIndexed;
  switch (scan.state) {
    case 'ready':
      tone = 'ok';
      parts.push('Ready');
      if (typeof count === 'number') parts.push(records(count));
      break;
    case 'scanning':
      parts.push('Scanning');
      if (typeof count === 'number' && count > 0) parts.push(records(count));
      break;
    case 'queued':
      parts.push('Queued');
      if (typeof scan.estimateMinutes === 'number' && scan.estimateMinutes > 0)
        parts.push(aboutMinutes(scan.estimateMinutes));
      break;
    case 'failed':
      tone = 'warn';
      parts.push(
        scan.reason === 'crm_not_connected'
          ? cleanupCrmName(scan.crmType === 'hubspot' ? 'hubspot' : undefined) +
              ' not connected'
          : 'Scan failed'
      );
      break;
    default:
      if (scan.completedAt) {
        parts.push('Scan out of date');
        if (typeof count === 'number') parts.push(records(count));
      } else parts.push('Not scanned yet');
  }
  if (scan.pendingRun) parts.push('Run waiting');
  for (const object of scan.unavailable ?? [])
    parts.push(cleanupObjectTitle(object) + ' unavailable');
  return { text: parts.join(' · '), tone };
}

/** How often a moving scan's status reads again. */
export const SCAN_POLL_MS = 30_000;

/** Run now on a cleanup play that waits for the scan it started (design 3.3). */
export const QUEUED_RUN_NOTICE = 'Run queued until the scan finishes';

/** Dry run on a cleanup play that waits for the scan it started. */
export const QUEUED_TEST_NOTICE = 'Dry run queued until the scan finishes';

/** A scan still moving, which the trigger's scan line polls. */
export function scanIsLive(scan: CrmScanStatusDto | undefined): boolean {
  return (
    scan?.state === 'scanning' ||
    scan?.state === 'queued' ||
    Boolean(scan?.pendingRun)
  );
}
